import {
  AI_TOOLKIT_ERROR_CODES,
  type AiToolkitErrorCode,
  type AiToolkitSerializedError,
} from "../contract";

export { AI_TOOLKIT_ERROR_CODES, type AiToolkitErrorCode, type AiToolkitSerializedError };

export type AiToolkitErrorInfo = Record<string, unknown>;

export class AiToolkitError extends Error {
  public readonly code: AiToolkitErrorCode;
  public readonly info?: AiToolkitErrorInfo;

  public constructor({
    code,
    message,
    info,
  }: {
    code: AiToolkitErrorCode;
    message: string;
    info?: AiToolkitErrorInfo;
  }) {
    super(message);

    this.name = new.target.name;
    this.code = code;
    this.info = info;
  }
}

export class AiToolkitUnknownToolError extends AiToolkitError {
  public constructor({ toolName }: { toolName: string }) {
    super({
      code: AI_TOOLKIT_ERROR_CODES.UnknownTool,
      message: `Unknown AI toolkit tool: ${toolName}`,
      info: { toolName },
    });
  }
}

export class AiToolkitInvalidInputError extends AiToolkitError {
  public constructor({
    toolName,
    message,
    info,
  }: {
    toolName: string;
    message: string;
    info?: AiToolkitErrorInfo;
  }) {
    super({
      code: AI_TOOLKIT_ERROR_CODES.InvalidInput,
      message,
      info: { toolName, ...info },
    });
  }
}

export class AiToolkitNoteNotFoundError extends AiToolkitError {
  public constructor({ noteId }: { noteId: string }) {
    super({
      code: AI_TOOLKIT_ERROR_CODES.NoteNotFound,
      message: `Local note not found: ${noteId}`,
      info: { noteId },
    });
  }
}

export class AiToolkitNoteContentNotFoundError extends AiToolkitError {
  public constructor({ noteId }: { noteId: string }) {
    super({
      code: AI_TOOLKIT_ERROR_CODES.NoteContentNotFound,
      message: `Local note content not found: ${noteId}`,
      info: { noteId },
    });
  }
}

export class AiToolkitAssistantTaskNotFoundError extends AiToolkitError {
  public constructor({ taskId }: { taskId: string }) {
    super({
      code: AI_TOOLKIT_ERROR_CODES.AssistantTaskNotFound,
      message: `Local assistant task not found: ${taskId}`,
      info: { taskId },
    });
  }
}

export class AiToolkitAssistantProjectNotFoundError extends AiToolkitError {
  public constructor({ projectId }: { projectId: string }) {
    super({
      code: AI_TOOLKIT_ERROR_CODES.AssistantProjectNotFound,
      message: `Local assistant project not found: ${projectId}`,
      info: { projectId },
    });
  }
}

export class AiToolkitAssistantFollowUpNotFoundError extends AiToolkitError {
  public constructor({ followUpId }: { followUpId: string }) {
    super({
      code: AI_TOOLKIT_ERROR_CODES.AssistantFollowUpNotFound,
      message: `Local assistant follow-up not found: ${followUpId}`,
      info: { followUpId },
    });
  }
}

export class AiToolkitAssistantNotFoundError extends AiToolkitError {
  public constructor() {
    super({
      code: AI_TOOLKIT_ERROR_CODES.AssistantNotFound,
      message: "Local assistant settings not found.",
    });
  }
}

export class AiToolkitCalendarEventNotFoundError extends AiToolkitError {
  public constructor({ calendarEventId }: { calendarEventId: string }) {
    super({
      code: AI_TOOLKIT_ERROR_CODES.CalendarEventNotFound,
      message: `Local calendar event not found: ${calendarEventId}`,
      info: { calendarEventId },
    });
  }
}

export class AiToolkitCollectionNotFoundError extends AiToolkitError {
  public constructor({ collectionId }: { collectionId: string }) {
    super({
      code: AI_TOOLKIT_ERROR_CODES.CollectionNotFound,
      message: `Local collection not found: ${collectionId}`,
      info: { collectionId },
    });
  }
}

export const serializeAiToolkitError = ({
  error,
}: {
  error: unknown;
}): AiToolkitSerializedError => {
  if (error instanceof AiToolkitError) {
    return {
      code: error.code,
      message: error.message,
      name: error.name,
      info: error.info,
    };
  }

  if (error instanceof Error) {
    return {
      code: AI_TOOLKIT_ERROR_CODES.ExecutionFailed,
      message: error.message,
      name: error.name,
    };
  }

  return {
    code: AI_TOOLKIT_ERROR_CODES.ExecutionFailed,
    message: String(error),
    name: "UnknownError",
  };
};
